import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { CreateAdicionalAction } from '@/application/actions';
import { useToast } from '@/presentation/contexts/ToastContext';
import { Button, Input } from '@/presentation/components/common';
import type { AdicionalFormData } from '@/domain/repositories/IAdicionalRepository';
import type { AdicionalTipo } from '@/domain/entities/Adicional';
import { ADICIONAL_TIPO_LABELS } from '@/domain/entities/Adicional';
import {
  defaultPackName,
  packDescriptionPlaceholder,
  packHelpText,
  packQuantityLabel,
  packUnitLabel,
} from './adicionalModalCopy';

interface CreateAdicionalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialTipo?: AdicionalTipo;
  /** Si true, no se puede cambiar el tipo (p.ej. solo Coofit Conexión). */
  lockTipo?: boolean;
}

const emptyForm = (tipo: AdicionalTipo = 'COOFIT_WHATSAPP_BOT'): AdicionalFormData => ({
  nombre: defaultPackName(tipo),
  descripcion:
    tipo === 'COOFIT_CONEXION'
      ? 'App paciente, push y WhatsApp con cupo mensual incluido.'
      : tipo === 'COOFIT_WHATSAPP_BOT'
        ? 'Bot que agenda, reprograma y cancela turnos por WhatsApp.'
        : tipo === 'WHATSAPP_MENSAJES'
          ? 'Recarga de mensajes WhatsApp solo para el mes en curso.'
          : '',
  precioUnitario: 0,
  precioUnitarioUsd: null,
  unidad: packUnitLabel(tipo),
  tipo,
  cupoMensajes: tipo === 'COOFIT_WHATSAPP_BOT' ? null : 500,
  esRecurrente: tipo !== 'WHATSAPP_MENSAJES',
  activo: true,
});

export const CreateAdicionalModal: React.FC<CreateAdicionalModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialTipo = 'COOFIT_WHATSAPP_BOT',
  lockTipo = false,
}) => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<AdicionalFormData>(emptyForm(initialTipo));

  useEffect(() => {
    if (isOpen) setFormData(emptyForm(initialTipo));
  }, [isOpen, initialTipo]);

  const setTipo = (tipo: AdicionalTipo) => {
    setFormData((prev) => ({
      ...prev,
      tipo,
      unidad: packUnitLabel(tipo),
      cupoMensajes:
        tipo === 'COOFIT_WHATSAPP_BOT'
          ? null
          : prev.cupoMensajes && prev.cupoMensajes > 0
            ? prev.cupoMensajes
            : 500,
      esRecurrente: tipo !== 'WHATSAPP_MENSAJES',
      nombre:
        !prev.nombre ||
        prev.nombre === defaultPackName('WHATSAPP_MENSAJES') ||
        prev.nombre === defaultPackName('EMAIL_MENSAJES') ||
        prev.nombre === defaultPackName('COOFIT_CONEXION')
          ? defaultPackName(tipo)
          : prev.nombre,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      showError(t('pages.adicionales.validation.nameRequired'));
      return;
    }
    if (formData.precioUnitario < 0) {
      showError(t('pages.adicionales.validation.priceMin'));
      return;
    }
    if (
      (formData.tipo === 'WHATSAPP_MENSAJES' ||
        formData.tipo === 'EMAIL_MENSAJES' ||
        formData.tipo === 'COOFIT_CONEXION') &&
      (!formData.cupoMensajes || formData.cupoMensajes < 1)
    ) {
      showError('Indica cuantos items incluye el pack');
      return;
    }

    setIsLoading(true);
    try {
      const action = container.get<CreateAdicionalAction>(TYPES.CreateAdicionalAction);
      const isMessagePack =
        formData.tipo === 'WHATSAPP_MENSAJES' || formData.tipo === 'EMAIL_MENSAJES';
      await action.execute({
        ...formData,
        esRecurrente: formData.tipo !== 'WHATSAPP_MENSAJES',
        cupoMensajes: formData.cupoMensajes,
        nombre:
          isMessagePack && formData.cupoMensajes
            ? formData.nombre.includes(String(formData.cupoMensajes))
              ? formData.nombre
              : `${formData.nombre} (${formData.cupoMensajes.toLocaleString('es-AR')} ${packUnitLabel(formData.tipo)})`
            : formData.nombre,
      });
      success(
        formData.tipo === 'COOFIT_CONEXION'
          ? t('pages.coofitConexion.activatedSuccess')
          : t('pages.adicionales.createdSuccess')
      );
      onSuccess();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Error al crear adicional');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const isPack =
    formData.tipo === 'WHATSAPP_MENSAJES' || formData.tipo === 'EMAIL_MENSAJES';
  const isConexion = formData.tipo === 'COOFIT_CONEXION';
  const isBot = formData.tipo === 'COOFIT_WHATSAPP_BOT';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500/70" onClick={onClose} />
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="px-5 pt-5 pb-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isConexion ? t('pages.coofitConexion.create') : t('pages.adicionales.create')}
                </h3>
                <button type="button" onClick={onClose} className="text-gray-400 text-xl">
                  ×
                </button>
              </div>

              <div className="space-y-4">
                {lockTipo ? (
                  <p className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-900 dark:border-teal-900 dark:bg-teal-950/40 dark:text-teal-100">
                    {packHelpText(formData.tipo)}
                  </p>
                ) : (
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tipo
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {(['WHATSAPP_MENSAJES', 'EMAIL_MENSAJES'] as AdicionalTipo[]).map((tipo) => (
                        <button
                          key={tipo}
                          type="button"
                          onClick={() => setTipo(tipo)}
                          className={`rounded-lg border px-2 py-2 text-sm ${
                            formData.tipo === tipo
                              ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/40 text-teal-800 dark:text-teal-200'
                              : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {ADICIONAL_TIPO_LABELS[tipo]}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">{packHelpText(formData.tipo)}</p>
                  </div>
                )}

                <Input
                  label={t('pages.adicionales.name')}
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder={
                    defaultPackName(formData.tipo) || t('pages.adicionales.namePlaceholder')
                  }
                  maxLength={100}
                  required
                />

                {isPack && (
                  <Input
                    label={packQuantityLabel(formData.tipo)}
                    type="number"
                    min="1"
                    value={formData.cupoMensajes ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cupoMensajes: parseInt(e.target.value, 10) || null,
                      })
                    }
                    required
                  />
                )}
                {isConexion ? (
                  <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-gray-600 dark:bg-gray-900/40 dark:text-slate-300">
                    Sin cupo de mensajes: el volumen se define con packs WhatsApp por clínica.
                  </p>
                ) : null}

                <Input
                  label={
                    isPack || isConexion || isBot
                      ? 'Precio mensual'
                      : t('pages.adicionales.unitPrice')
                  }
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.precioUnitario}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      precioUnitario: parseFloat(e.target.value) || 0,
                    })
                  }
                  required
                />
                <Input
                  label={t('pages.adicionales.unitPriceUsd')}
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.precioUnitarioUsd ?? ''}
                  placeholder={t('pages.plans.priceUsdPlaceholder')}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      precioUnitarioUsd: e.target.value === '' ? null : parseFloat(e.target.value) || 0,
                    })
                  }
                />

                {!isPack && !isConexion && !isBot && (
                  <Input
                    label={t('pages.adicionales.unit')}
                    type="text"
                    value={formData.unidad}
                    onChange={(e) => setFormData({ ...formData, unidad: e.target.value })}
                    placeholder={t('pages.adicionales.unitPlaceholder')}
                    maxLength={50}
                  />
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('pages.adicionales.description')}
                  </label>
                  <textarea
                    value={formData.descripcion}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                    rows={2}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    maxLength={500}
                    placeholder={packDescriptionPlaceholder(formData.tipo)}
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={formData.activo}
                      onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                      className="rounded"
                    />
                    {t('pages.adicionales.active')}
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-gray-900/50 px-5 py-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button type="button" variant="secondary" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" isLoading={isLoading}>
                {t('common.save')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
